import { useSearchParams } from '@remix-run/react';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import { useTeamInvites } from '~/lib/hooks/useTeamInvites';
import { INVITE_TOKEN_SEARCH_PARAM } from '~/lib/stores/pricing/config';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';

export function InvitationBanner({ user }: { user: UserData }) {
  const { isAccepting, isDenying, accepted, dismissed, dismiss, accept, deny, invite } = useTeamInvites({});
  const [searchParams] = useSearchParams();

  /**
   * We want to hide banner during accepting invite from email.
   */
  const shouldHideBanner = !!searchParams.get(INVITE_TOKEN_SEARCH_PARAM);

  if (!user?.featureFlags.teamManagement || !invite || dismissed || shouldHideBanner) {
    return null;
  }

  return (
    <div
      className={classNames(
        accepted
          ? 'border-bolt-ds-success-container bg-bolt-ds-successContainer'
          : 'border-bolt-ds-brand-container bg-bolt-ds-surface',
        'fixed z-max bottom-8 right-8',
        'flex items-start gap-3',
        'w-111 max-w-full',
        'pl-4 pr-8 py-4',
        'border border-bolt-ds-borderOutline rounded-md',
        'select-none',
        'shadow-sm',
      )}
    >
      {accepted ? (
        <span className="i-ph-check-circle-light text-xl text-bolt-ds-success" />
      ) : (
        <span className="i-heroicons:envelope text-xl text-bolt-ds-brand" />
      )}

      <div className="flex flex-col gap-1 max-w-90">
        {accepted ? (
          <span className="font-semibold text-bolt-ds-success text-sm">You're in!</span>
        ) : (
          <span className="text-bolt-ds-primary font-semibold text-sm">
            You've been invited to join <b>{invite?.organization.name}</b> team
          </span>
        )}

        <div className="text-sm text-bolt-elements-textSecondary">
          {accepted
            ? 'You have successfully joined the team.'
            : 'If you accept this request, a distinct Team account will be created for you.'}
        </div>

        {!accepted && (
          <div className="flex gap-3 items-center mt-2">
            <Button
              size="sm"
              variant="link-color"
              onClick={accept}
              disabled={isAccepting || isDenying}
              loading={isAccepting}
            >
              Accept invitation
            </Button>

            <Button
              size="sm"
              variant="link-gray"
              className="text-bolt-ds-textSecondary"
              onClick={deny}
              disabled={isAccepting || isDenying}
              loading={isDenying}
            >
              Deny invitation
            </Button>
          </div>
        )}
      </div>

      <IconButton icon="i-ph:x" size="2xs" variant="ghost" className="absolute top-2 right-2" onClick={dismiss} />
    </div>
  );
}
